import React, { useState } from "react";
import { Plus, X } from "lucide-react";

// Comma/enter/blur committed chip input. Empty array semantically reads as
// "all values" when paired with the ChipScope helper below.
const ChipInput = ({ values = [], onChange, placeholder = "Add…", disabled, mono }) => {
  const [draft, setDraft] = useState("");

  const commit = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };

  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 min-h-9 px-2 py-1.5 bg-white border border-[#D9DEE6] rounded-md focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10 ${
        disabled ? "bg-[#F1F4F8] cursor-not-allowed" : ""
      }`}
    >
      {values.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-[#EEF2FF] text-[#3730A3] ${
            mono ? "font-mono" : ""
          }`}
        >
          {v}
          {!disabled && (
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-[#6366F1] hover:text-[#312E81]"
            >
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      <input
        type="text"
        value={draft}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && !draft && values.length) {
            remove(values.length - 1);
          }
        }}
        className={`flex-1 min-w-[80px] outline-none text-[13px] bg-transparent ${
          mono ? "font-mono" : ""
        }`}
      />
    </div>
  );
};

// Scope chip group with ALL toggle — empty array internally means "ALL".
// The on-wire shape uses the explicit string "ALL" sentinel for clarity.
export const ChipScope = ({ value = ["ALL"], onChange, placeholder, disabled, label }) => {
  const isAll = !Array.isArray(value) || value.length === 0 || value.includes("ALL");
  const items = isAll ? [] : value;

  const setAll = () => onChange(["ALL"]);
  const setSpecific = (next) => onChange(next.length === 0 ? ["ALL"] : next);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-[12px] font-semibold text-[#334155]">{label}</label>
          <button
            type="button"
            disabled={disabled}
            onClick={isAll ? () => onChange([]) : setAll}
            className={`text-[11px] font-semibold ${
              isAll ? "text-primary" : "text-[#64748B] hover:text-primary"
            }`}
          >
            {isAll ? "Restrict" : "Use ALL"}
          </button>
        </div>
      )}
      {isAll ? (
        <div className="flex items-center gap-2 px-2 py-1.5 min-h-9 bg-[#F1F4F8] border border-[#E6E9EF] rounded-md">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11.5px] font-semibold bg-white text-[#475569] border border-[#D9DEE6]">
            ALL
          </span>
          <span className="text-[11px] text-[#94A3B8]">applies everywhere</span>
        </div>
      ) : (
        <ChipInput
          values={items}
          onChange={setSpecific}
          placeholder={placeholder}
          disabled={disabled}
          mono
        />
      )}
    </div>
  );
};

export default ChipInput;
